"use client";

// ============================================================
// CLUBHUB – Nachrichten: Zone 2, was drinsteht
// ============================================================
// Vorlage, Absender, Betreff, Text mit Werkzeugleiste und
// Platzhaltern, Anhaenge. Die Reihenfolge ist die des Mailkopfs in
// der Vorschau daneben (Von, Antwort an, Betreff, Text) — so steht
// jedes Feld auf der Hoehe dessen, was es dort veraendert. Der Text
// fuellt den Platz bis zu den Anhaengen.
//
// Fuer SMS faellt weg, was eine SMS nicht traegt (Betreff,
// Auszeichnung, Anhang); dafuer zaehlt ein Zaehler die Zeichen und
// nennt das Zeichen, das Unicode erzwingt. Beim Brief steht der
// Unterzeichner unten — wie auf dem Papier.
// ============================================================
import { useRef } from "react";
import Link from "next/link";
import { Paperclip, TriangleAlert, X } from "lucide-react";
import type { MailingChannel } from "@/generated/prisma/enums";
import { Input } from "@/components/ui/input";
import { Select, type SelectOption } from "@/components/ui/select";
import { toast } from "@/components/ui/toast";
import { alsText } from "@/lib/nachrichten/markdown";
import { smsLaenge } from "@/lib/nachrichten/sms";
import type { AnhangView, VereinsangabenView } from "@/lib/nachrichten/views";
import { MarkdownFeld } from "./markdown-feld";
import { dateigroesse } from "./vorschau";
import { Angabe } from "./zone";

export function SmsZaehler({ text }: { text: string }) {
  const l = smsLaenge(alsText(text));
  return (
    <div className="text-caption flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="text-text-secondary numeric">
        {l.zeichen} Zeichen · {l.teile === 1 ? "passt in eine SMS" : `${l.teile} Teile`} · noch{" "}
        {l.frei} bis zum nächsten Teil
      </span>
      {l.unicode && (
        <span className="text-warning flex items-center gap-1">
          <TriangleAlert className="h-3.5 w-3.5" aria-hidden />
          {l.fremd.map((z) => `„${z}“`).join(" ")} {l.fremd.length === 1 ? "erzwingt" : "erzwingen"}{" "}
          Unicode — dann passen nur 70 Zeichen in eine SMS
        </span>
      )}
    </div>
  );
}

function Anhaenge({
  liste,
  channel,
  onChange,
}: {
  liste: AnhangView[];
  channel: MailingChannel;
  onChange: (l: AnhangView[]) => void;
}) {
  const wahl = useRef<HTMLInputElement>(null);
  return (
    <Angabe
      titel={channel === "LETTER" ? "Anlagen" : "Anhänge"}
      hinweis={
        channel === "LETTER"
          ? "PDF-Dateien werden hinter den Brief gedruckt."
          : "Gehen mit jeder Mail hinaus. Große Dateien besser als Verweis in den Text."
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        {liste.map((a) => (
          <span
            key={a.id}
            className="bg-bg-secondary ring-border-subtle text-body-sm flex max-w-full items-center gap-2 rounded-lg py-1.5 pr-1.5 pl-2.5 ring-1 ring-inset"
          >
            <Paperclip className="text-text-tertiary h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="text-text-primary min-w-0 truncate">{a.name}</span>
            <span className="text-caption text-text-tertiary numeric shrink-0">
              {dateigroesse(a.size)}
            </span>
            <button
              type="button"
              onClick={() => onChange(liste.filter((x) => x.id !== a.id))}
              aria-label={`${a.name} entfernen`}
              className="text-text-tertiary hover:bg-bg-tertiary hover:text-text-primary grid h-6 w-6 place-items-center rounded-md"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={() => wahl.current?.click()}
          className="text-body-sm text-text-secondary hover:bg-bg-tertiary hover:text-text-primary flex h-9 items-center gap-1.5 rounded-lg px-2.5 transition-colors"
        >
          <Paperclip className="h-4 w-4" aria-hidden />
          Datei anhängen
        </button>
        <input
          ref={wahl}
          type="file"
          multiple
          accept={channel === "LETTER" ? "application/pdf" : undefined}
          className="sr-only"
          tabIndex={-1}
          onChange={(e) => {
            const dateien = [...(e.target.files ?? [])];
            if (!dateien.length) return;
            // BACKEND: Dateien hochladen (Ablage wie Dokumente) und am Mailing als Anhang verknuepfen
            onChange([
              ...liste,
              ...dateien.map((d, i) => ({
                id: `neu-${Date.now()}-${i}`,
                name: d.name,
                size: d.size,
              })),
            ]);
            toast.success(
              dateien.length === 1 ? "Datei angehängt" : `${dateien.length} Dateien angehängt`,
            );
            e.target.value = "";
          }}
        />
      </div>
    </Angabe>
  );
}

export function InhaltZone({
  modus,
  channel,
  subject,
  body,
  fromName,
  replyTo,
  attachments,
  templateId,
  vorlagenOptionen,
  antwortOptionen,
  verein,
  fehler,
  onVorlage,
  onSubject,
  onBody,
  onFromName,
  onReplyTo,
  onAttachments,
}: {
  modus: "nachricht" | "vorlage";
  channel: MailingChannel;
  subject: string;
  body: string;
  fromName: string;
  replyTo: string;
  attachments: AnhangView[];
  templateId: string | null;
  vorlagenOptionen: SelectOption[];
  antwortOptionen: SelectOption[];
  verein: VereinsangabenView;
  fehler: { subject?: string; body?: string };
  onVorlage: (id: string) => void;
  onSubject: (s: string) => void;
  onBody: (s: string) => void;
  onFromName: (s: string) => void;
  onReplyTo: (s: string) => void;
  onAttachments: (l: AnhangView[]) => void;
}) {
  const sms = channel === "SMS";
  return (
    <>
      {modus === "nachricht" && (
        <Angabe
          titel="Vorlage"
          hinweis={
            vorlagenOptionen.length ? (
              <>
                Übernimmt Betreff und Text.{" "}
                <Link
                  href="/nachrichten/vorlagen"
                  className="text-text-secondary hover:text-accent underline underline-offset-2"
                >
                  Vorlagen verwalten
                </Link>
              </>
            ) : (
              "Für diesen Kanal gibt es noch keine Vorlage."
            )
          }
        >
          <Select
            options={vorlagenOptionen}
            value={templateId ?? ""}
            onChange={onVorlage}
            placeholder="Ohne Vorlage"
            disabled={vorlagenOptionen.length === 0}
            aria-label="Vorlage wählen"
          />
        </Angabe>
      )}

      {modus === "nachricht" && channel === "EMAIL" && (
        <div className="grid gap-5 sm:grid-cols-2">
          <Input
            id="nachricht-von"
            label="Absendername"
            value={fromName}
            onChange={(e) => onFromName(e.target.value)}
            hint={`Geht von ${verein.absenderAdresse} hinaus.`}
            maxLength={80}
          />
          <Select
            id="nachricht-antwort"
            label="Antworten gehen an"
            options={antwortOptionen}
            value={replyTo}
            onChange={onReplyTo}
            hint="Antworten landen dort — kein No-reply-Postfach."
          />
        </div>
      )}

      {sms ? (
        <Input
          id="nachricht-betreff"
          label="Bezeichnung"
          value={subject}
          onChange={(e) => onSubject(e.target.value)}
          error={fehler.subject}
          hint="Nur für die Liste — eine SMS hat keinen Betreff."
          placeholder="z. B. Trainingsausfall Dienstag"
          maxLength={150}
        />
      ) : (
        <Input
          id="nachricht-betreff"
          label="Betreff"
          value={subject}
          onChange={(e) => onSubject(e.target.value)}
          error={fehler.subject}
          hint={
            subject.length > 70
              ? `${subject.length} Zeichen — im Posteingang wird nach etwa 60 Zeichen abgeschnitten.`
              : "Platzhalter gehen auch hier, etwa {{vorname}}."
          }
          placeholder="Worum geht es?"
          maxLength={150}
        />
      )}

      <Angabe
        titel="Text"
        fehler={fehler.body}
        htmlFor="nachricht-text"
        className="flex flex-1 flex-col"
      >
        <MarkdownFeld
          id="nachricht-text"
          className="flex-1"
          value={body}
          onChange={onBody}
          fehler={fehler.body}
          sms={sms}
          platzhalterText={
            sms
              ? "Kurz und klar — wer, was, wann."
              : "{{anrede}},\n\nschreib hier deinen Text. Die Vorschau zeigt, wie er bei jedem Empfänger ankommt."
          }
        />
        {sms && <SmsZaehler text={body} />}
      </Angabe>

      {!sms && <Anhaenge liste={attachments} channel={channel} onChange={onAttachments} />}

      {modus === "nachricht" && channel === "LETTER" && (
        <Input
          id="nachricht-von"
          label="Unterzeichnet von"
          value={fromName}
          onChange={(e) => onFromName(e.target.value)}
          hint="Steht unter dem Gruß. Briefkopf und Absenderzeile kommen aus den Vereinsangaben."
          maxLength={80}
        />
      )}
      {modus === "nachricht" && channel === "SMS" && (
        <p className="text-caption text-text-tertiary">
          Absenderkennung:{" "}
          <span className="text-text-secondary font-medium">{verein.smsKennung}</span> —
          einzustellen unter{" "}
          <Link
            href="/einstellungen/kommunikation/versand"
            className="hover:text-accent underline underline-offset-2"
          >
            Einstellungen, Kommunikation
          </Link>
          .
        </p>
      )}
    </>
  );
}
